/**
 * Tailwind Plugin - smart auto text contrast
 *
 * This plugin automatically ensures good contrast:
 * - Sets default text color based on background luminance
 * - Only overrides explicit text colors if they would have poor contrast
 * - Allows good contrast combinations to work as intended
 */

import plugin from 'tailwindcss/plugin'
import type { CSSRuleObject } from 'tailwindcss/types/config'

interface RGB {
  r: number
  g: number
  b: number
}

interface ColorValue {
  DEFAULT?: string
  [key: string]: string | ColorValue | undefined
}

type ThemeColors = Record<string, string | ColorValue>

/**
 * Calculates the relative luminance of a color using the WCAG formula
 * @param r - Red component (0-255)
 * @param g - Green component (0-255)
 * @param b - Blue component (0-255)
 * @returns Luminance value between 0 (darkest) and 1 (brightest)
 */
export const getLuminance = (r: number, g: number, b: number): number => {
  const [rs, gs, bs] = [r, g, b].map((c: number): number => {
    const normalized = c / 255
    return normalized <= 0.03928
      ? normalized / 12.92
      : Math.pow((normalized + 0.055) / 1.055, 2.4)
  })

  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs
}

/**
 * Calculates contrast ratio between two colors
 * WCAG AA requires 4.5:1 for normal text
 * WCAG AAA requires 7:1 for normal text
 */
const getContrastRatio = (l1: number, l2: number): number => {
  const lighter = Math.max(l1, l2)
  const darker = Math.min(l1, l2)
  return (lighter + 0.05) / (darker + 0.05)
}

/**
 * Converts a hexadecimal color string to RGB values
 * @param hex - Hex color string (e.g., "#ff0000" or "ff0000")
 * @returns RGB object or null if invalid hex format
 */
const hexToRgb = (hex: string): RGB | null => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  if (!result) return null

  return {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16),
  }
}

/**
 * Type guard to check if a value is a string
 */
const isString = (value: unknown): value is string => {
  return typeof value === 'string'
}

/**
 * Type guard to check if a value is a ColorValue object
 */
const isColorValue = (value: unknown): value is ColorValue => {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

/**
 * Main plugin that creates background utilities with automatic text contrast
 */
const autoContrastPlugin = plugin(({ addBase, addUtilities, theme }) => {
  const colors = theme('colors') as ThemeColors | undefined

  if (!colors || typeof colors !== 'object') {
    console.warn('No colors found in theme')
    return
  }

  const processedColors = new Map<string, { hex: string; luminance: number }>()

  const processColors = (obj: ThemeColors, prefix = '') => {
    Object.entries(obj).forEach(([key, value]) => {
      if (key === 'inherit' || key === 'current' || key === 'transparent') {
        return
      }

      const className = prefix ? `${prefix}-${key}` : key

      if (isString(value) && value.startsWith('#')) {
        const rgb = hexToRgb(value)
        if (rgb) {
          const luminance = getLuminance(rgb.r, rgb.g, rgb.b)
          processedColors.set(className, { hex: value, luminance })
        }
      } else if (isColorValue(value)) {
        if (value.DEFAULT && isString(value.DEFAULT)) {
          const rgb = hexToRgb(value.DEFAULT)
          if (rgb) {
            const luminance = getLuminance(rgb.r, rgb.g, rgb.b)
            processedColors.set(className, { hex: value.DEFAULT, luminance })
          }
        }
        const nestedColors: ThemeColors = {}
        Object.entries(value).forEach(([nestedKey, nestedValue]) => {
          if (
            nestedKey !== 'DEFAULT' &&
            (isString(nestedValue) || isColorValue(nestedValue))
          ) {
            nestedColors[nestedKey] = nestedValue
          }
        })
        processColors(nestedColors, className)
      }
    })
  }

  processColors(colors)

  const baseStyles: Record<string, CSSRuleObject> = {}
  const utilityStyles: Record<string, CSSRuleObject> = {}
  const hoverUtilities: Record<string, CSSRuleObject> = {}

  const blackColor = theme('colors.black') || '#000000'
  const whiteColor = theme('colors.white') || '#ffffff'

  processedColors.forEach(({ luminance }, className) => {
    const shouldUseDarkText = luminance > 0.18
    const defaultTextColor = shouldUseDarkText ? blackColor : whiteColor

    baseStyles[`.bg-${className}`] = {
      color: defaultTextColor,
    }

    const whiteContrast = getContrastRatio(luminance, 1)
    const blackContrast = getContrastRatio(luminance, 0)

    if (whiteContrast < 4.5 && blackContrast >= 4.5) {
      utilityStyles[`.bg-${className}.text-white`] = {
        color: `${blackColor} !important`,
      }
    }

    if (blackContrast < 4.5 && whiteContrast >= 4.5) {
      utilityStyles[`.bg-${className}.text-black`] = {
        color: `${whiteColor} !important`,
      }
    }

    hoverUtilities[`.hover\\:bg-${className}:hover`] = {
      color: `${defaultTextColor} !important`,
    }
  })

  baseStyles['.bg-black'] = { color: whiteColor }
  baseStyles['.bg-white'] = { color: blackColor }

  hoverUtilities['.hover\\:bg-black:hover'] = {
    color: `${whiteColor} !important`,
  }
  hoverUtilities['.hover\\:bg-white:hover'] = {
    color: `${blackColor} !important`,
  }

  addBase(baseStyles)
  addUtilities(utilityStyles)
  addUtilities(hoverUtilities)

  const superHoverUtilities: Record<string, CSSRuleObject> = {}

  processedColors.forEach(({ luminance }, className) => {
    const shouldUseDarkText = luminance > 0.18
    const textColor = shouldUseDarkText ? blackColor : whiteColor

    superHoverUtilities[`.text-white.hover\\:bg-${className}:hover`] = {
      color: `${textColor} !important`,
    }

    superHoverUtilities[`.text-black.hover\\:bg-${className}:hover`] = {
      color: `${textColor} !important`,
    }
  })

  addUtilities(superHoverUtilities)
})

export default autoContrastPlugin
